import { useState, type ReactElement } from 'react';
import { Skeleton, Typography, Chip, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { useDashboard } from '../DashboardContext';
import type { RecentTransaction } from '../DashboardContext';
import { formatRecentTransactionDate, formatRecentTransactionTimestamp } from '../dashboardHelpers';
import {
  hasRecentReportReference,
  resolveRecentReportKind,
  toRecentReportBundle,
} from '../recentTransactionReport';
import { styleForRecentTransaction } from '../recentTransactionMeta';
import { useTranslation } from 'react-i18next';
import DashboardWidget, { useDashboardWidgetScrollHeight } from './DashboardWidget';
import RecentTransactionReports, { type RecentReportView } from './RecentTransactionReports';

const cellSx = { fontSize: '0.8rem', whiteSpace: 'nowrap' as const };

const RecentTransactionsTable = () => {
  const { recentTransactions, currency } = useDashboard();
  const { t } = useTranslation();
  const theme = useTheme();
  const scrollHeight = useDashboardWidgetScrollHeight(400);
  const [reportView, setReportView] = useState<RecentReportView | null>(null);

  const openReport = (tx: RecentTransaction) => {
    if (!hasRecentReportReference(tx)) return;
    setReportView({
      kind: resolveRecentReportKind(tx),
      bundle: toRecentReportBundle(tx),
    });
  };

  const closeReport = () => setReportView(null);

  const formatAmount = (value: number) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;
  };

  if (recentTransactions.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <>
    <TableContainer sx={{ maxHeight: scrollHeight, height: scrollHeight === '100%' ? '100%' : undefined, flex: scrollHeight === '100%' ? 1 : undefined }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.amount')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.depot')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.date')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.reference')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.description')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.user')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.createdAt')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('common:dashboard.recent.updatedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTransactions.map((tx, idx) => {
                const cfg = styleForRecentTransaction(tx.type, theme.palette.mode);
                return (
                  <TableRow key={`${tx.id}-${idx}`} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Chip
                        icon={cfg.icon as ReactElement}
                        label={t(`common:dashboard.recent.types.${tx.type}`)}
                        size="small"
                        color={cfg.chipColor}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                      {formatAmount(tx.amount)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tx.depot || '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{formatRecentTransactionDate(tx.date)}</TableCell>
                    <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }}>
                      {hasRecentReportReference(tx) ? (
                        <button
                          type="button"
                          style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                          onClick={() => openReport(tx)}
                        >
                          {tx.reference || tx.batch || tx.id}
                        </button>
                      ) : (
                        tx.reference || '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tx.description || '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tx.user || '—'}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{formatRecentTransactionTimestamp(tx.createdAt)}</TableCell>
                    <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{formatRecentTransactionTimestamp(tx.updatedAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
    </TableContainer>
      <RecentTransactionReports reportView={reportView} onClose={closeReport} />
    </>
  );
};

const RecentTransactions = () => {
  const { sectionLoading } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.activity) {
    return (
      <DashboardWidget title={t('common:dashboard.recent.title')} helpKey="recent.title" chartHeight={400} scrollable>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="text" width="100%" height={48} sx={{ mt: 1 }} />
        ))}
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      title={t('common:dashboard.recent.title')}
      helpKey="recent.title"
      chartHeight={400}
      scrollable
    >
      <RecentTransactionsTable />
    </DashboardWidget>
  );
};

export default RecentTransactions;